import * as React from "react"
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconCircleCheckFilled,
  IconDotsVertical,
  IconGripVertical,
  IconLayoutColumns,
  IconLoader,
  IconPlus,
} from "@tabler/icons-react"
import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { toast } from "sonner"
import { z } from "zod"

import { useIsMobile } from "@/hooks/use-mobile"
import { Head, useForm, usePage, router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { IconArrowsSort, IconSearch, IconCopy, IconTrash, IconCheck, IconX, IconFile } from "@tabler/icons-react"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogTrigger
} from "../components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export const schema = z.object({
  id: z.number(),
  header: z.string(),
  type: z.string(),
  status: z.string(),
  target: z.string(),
  limit: z.string(),
  reviewer: z.string(),
  cdn_url: z.string().optional(),
  file_name: z.string(),
  tag: z.string().optional(),
  file_size: z.string().optional(),
  content_type: z.string().optional(),
})

function DragHandle({ id }: { id: string | number }) {
  const { attributes, listeners } = useSortable({
    id,
  })

  return (
    <Button
      {...attributes}
      {...listeners}
      variant="ghost"
      size="icon"
      className="text-muted-foreground size-7 hover:bg-transparent"
    >
      <IconGripVertical className="text-muted-foreground size-3" />
      <span className="sr-only">Drag to reorder</span>
    </Button>
  )
}


function DraggableRow({
  row,
  onFileSelect,
  selectedFileId,
}: {
  row: Row<z.infer<typeof schema>>;
  onFileSelect: (file: z.infer<typeof schema>) => void;
  selectedFileId: number | string | null;
}) {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id,
  })

  const isSelected = row.original.id === selectedFileId;

  return (
    <TableRow
      onClick={() => onFileSelect(row.original)}
      className={cn(
        "relative z-0 data-[dragging=true]:z-10 data-[dragging=true]:opacity-80 cursor-pointer",
        isSelected && "bg-blue-100 dark:bg-blue-900" // Example selection highlight
      )}
      data-state={isSelected && "selected"}
      data-dragging={isDragging}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition,
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  )
}

export function DataTable({
  data: initialData,
  onFileSelect,
  selectedFileId,
  onFileDelete,
  pagination: serverPagination,
  filters: serverFilters,
  sorting: serverSorting,
  onPaginationChange,
  onFiltersChange,
  onSortingChange,
  availableTags,
}: {
  data: z.infer<typeof schema>[];
  onFileSelect: (file: z.infer<typeof schema>) => void;
  selectedFileId: number | string | null;
  onFileDelete?: (fileId: number) => void;
  pagination?: {
    page: number;
    pages: number;
    count: number;
    items: number;
    from: number;
    to: number;
    prev: number | null;
    next: number | null;
  };
  filters?: {
    search: string;
    content_type: string;
    tag: string;
  };
  sorting?: {
    column: string;
    direction: string;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  onFiltersChange?: (filters: { search: string; content_type: string; tag: string }) => void;
  onSortingChange?: (sorting: { column: string; direction: string }) => void;
  availableTags?: string[];
}) {
  const columns: ColumnDef<z.infer<typeof schema>>[] = [
    {
      accessorKey: "file_name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-2 -ml-2"
        >
          Name
          <IconArrowsSort className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const isImage = row.original.content_type?.startsWith("image/");
        return (
          <div className="flex items-center gap-2 max-w-[300px]">
            {isImage && row.original.cdn_url ? (
              <img
                src={`${row.original.cdn_url}?w=30&h=30`}
                alt={row.original.file_name} // Use file_name for alt text
                className="w-[30px] h-[30px] object-cover rounded flex-shrink-0"
              />
            ) : (
              <IconFile className="w-[30px] h-[30px] text-gray-400 flex-shrink-0" />
            )}
            <span className="truncate">{row.original.file_name}</span>
          </div>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, id, value) => {
        return String(row.getValue(id)).toLowerCase().includes(String(value).toLowerCase())
      },
    },
    {
      accessorKey: "content_type",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="-ml-4"
        >
          Type
          <IconArrowsSort className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="min-w-[100px]">
          {row.getValue("content_type")}
        </div>
      ),
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: "file_size",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="-ml-4"
        >
          Size
          <IconArrowsSort className="ml-2 h-4 w-4" />
        </Button>
      ),
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: "tag",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="-ml-4"
        >
          Tag
          <IconArrowsSort className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="max-w-[150px] truncate">
          {row.getValue("tag") || "No tag"}
        </Badge>
      ),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, id, value) => {
        return String(row.getValue(id)).toLowerCase().includes(String(value).toLowerCase())
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0 group relative"
            onClick={async (e) => {
              e.stopPropagation();
              const url = row.original.cdn_url;
              if (!url) return;
              
              const button = e.currentTarget;
              const copyIcon = button.querySelector('.copy-icon');
              const checkIcon = button.querySelector('.check-icon');
              
              try {
                await navigator.clipboard.writeText(url);
                // Show success feedback
                if (copyIcon) copyIcon.classList.add('hidden');
                if (checkIcon) checkIcon.classList.remove('hidden');
                
                setTimeout(() => {
                  if (copyIcon) copyIcon.classList.remove('hidden');
                  if (checkIcon) checkIcon.classList.add('hidden');
                }, 2000);
              } catch (err) {
                console.error('Failed to copy URL: ', err);
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = url;
                document.body.appendChild(textArea);
                textArea.select();
                try {
                  document.execCommand('copy');
                  // Show success feedback for fallback
                  if (copyIcon) copyIcon.classList.add('hidden');
                  if (checkIcon) checkIcon.classList.remove('hidden');
                  
                  setTimeout(() => {
                    if (copyIcon) copyIcon.classList.remove('hidden');
                    if (checkIcon) checkIcon.classList.add('hidden');
                  }, 2000);
                } catch (err) {
                  console.error('Fallback copy failed: ', err);
                }
                document.body.removeChild(textArea);
              }
            }}
            disabled={!row.original.cdn_url}
            title="Copy URL to clipboard"
          >
            <IconCopy className="copy-icon h-4 w-4 transition-all" />
            <IconCheck className="check-icon hidden h-4 w-4 text-green-500" />
            <span className="sr-only">Copy URL</span>
          </Button>
          <Dialog>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={(e) => {
                console.log(row.original.id)
                e.stopPropagation();
                fileToDeleteRef.current = {
                  id: row.original.id,
                  name: row.original.file_name
                };
              }}
              asChild
            >
              <DialogTrigger>
                <IconTrash className="h-4 w-4" />
                <span className="sr-only">Delete</span>
              </DialogTrigger>
            </Button>
            
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete file</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete <span className="font-medium">{row.original.file_name}</span>?
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" asChild>
                  <DialogClose>
                    <IconX className="mr-2 h-4 w-4" />
                    Cancel
                  </DialogClose>
                </Button>
                <Button
                  variant="destructive"
                  className="sm:ml-2"
                  asChild
                >
                  <DialogClose
                    onClick={async () => {
                      if (!fileToDeleteRef.current) return;
                      console.log(fileToDeleteRef.current.id)
                      try {
                        // Send DELETE request to the server
                        router.delete(`/react_images/${fileToDeleteRef.current.id}`, {
                          onSuccess: () => {
                            // Call the onFileDelete callback if provided
                            if (onFileDelete && fileToDeleteRef.current) {
                              const fileId = typeof fileToDeleteRef.current.id === 'string' 
                                ? parseInt(fileToDeleteRef.current.id, 10) 
                                : fileToDeleteRef.current.id;
                              onFileDelete(fileId);
                            }
                          },
                          onError: () => {
                            console.error('Error deleting file');
                          }
                        });
                      } catch (error) {
                        console.error('Error deleting file:', error);
                      }
                    }}
                  >
                    <IconTrash className="mr-2 h-4 w-4" />
                    Delete
                  </DialogClose>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ]

  const [data, setData] = React.useState(() => [...initialData])

  React.useEffect(() => {
    setData([...initialData]);
  }, [initialData]);

  const fileToDeleteRef = React.useRef<{id: string | number, name: string} | null>(null)
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  // For server-side sorting, use server state and trigger callbacks
  const sorting = React.useMemo(() => {
    if (serverSorting?.column) {
      return [{
        id: serverSorting.column,
        desc: serverSorting.direction === 'desc'
      }]
    }
    return []
  }, [serverSorting])

  const setSorting = React.useCallback((updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater
    if (newSorting.length > 0) {
      const sortColumn = newSorting[0]
      onSortingChange?.({
        column: sortColumn.id,
        direction: sortColumn.desc ? 'desc' : 'asc'
      })
    } else {
      // Clear sorting
      onSortingChange?.({
        column: '',
        direction: 'asc'
      })
    }
  }, [sorting, onSortingChange])
  // For server-side pagination, use server state and trigger callbacks
  const pagination = React.useMemo(() => ({
    pageIndex: serverPagination ? serverPagination.page - 1 : 0,
    pageSize: serverPagination ? serverPagination.items : 20,
  }), [serverPagination])

  const setPagination = React.useCallback((updater: any) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    onPaginationChange?.(newPagination)
  }, [pagination, onPaginationChange])

  const sortableId = React.useId()
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  )

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => data?.map(({ id }) => id) || [],
    [data]
  )

  // For server-side filtering, use server state and trigger callbacks
  const [globalFilter, setGlobalFilter] = React.useState(serverFilters?.search || '')
  
  // Update local filter state when server filters change
  React.useEffect(() => {
    setGlobalFilter(serverFilters?.search || '')
  }, [serverFilters?.search])

  // Debounced filter change handler
  const debouncedFilterChange = React.useCallback(
    debounce((searchValue: string) => {
      onFiltersChange?.({
        search: searchValue,
        content_type: serverFilters?.content_type || '',
        tag: serverFilters?.tag || '',
      })
    }, 300),
    [onFiltersChange, serverFilters]
  )

  // Handle global filter changes
  const handleGlobalFilterChange = React.useCallback((value: string) => {
    setGlobalFilter(value)
    debouncedFilterChange(value)
  }, [debouncedFilterChange])

  // Handle tag filter changes
  const handleTagFilterChange = React.useCallback((tagValue: string) => {
    onFiltersChange?.({
      search: serverFilters?.search || '',
      content_type: serverFilters?.content_type || '',
      tag: tagValue === 'all' ? '' : tagValue,
    })
  }, [onFiltersChange, serverFilters])

  // Simple debounce utility
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
    let timeout: NodeJS.Timeout | null = null
    return ((...args: any[]) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }) as T
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
      pagination,
    },
    // Use server-side filtering and pagination
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getRowId: (row) => row.id.toString(),
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    // Disable client-side filtering, pagination, and sorting since we're doing it server-side
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    // Set the page count from server pagination
    pageCount: serverPagination?.pages || -1,
    // Keep these for UI elements that don't affect server queries
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      setData((data) => {
        const oldIndex = dataIds.indexOf(active.id)
        const newIndex = dataIds.indexOf(over.id)
        return arrayMove(data, oldIndex, newIndex)
      })
    }
  }

  return (
    <div className="w-full flex-col justify-start gap-6 ">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-4 lg:px-6">
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <div>
            <Label htmlFor="search" className="sr-only">Search</Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconSearch className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                id="search"
                placeholder="Search files..."
                value={globalFilter}
                onChange={(e) => handleGlobalFilterChange(e.target.value)}
                className="pl-10 w-full md:w-64"
              />
            </div>
          </div>
          {availableTags && availableTags.length > 0 && (
            <div>
              <Label htmlFor="tag-filter" className="sr-only">Filter by tag</Label>
              <Select
                value={serverFilters?.tag || "all"}
                onValueChange={handleTagFilterChange}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  <SelectItem value="no_tag">No tag</SelectItem>
                  {availableTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <IconLayoutColumns />
                <span className="hidden lg:inline">Customize Columns</span>
                <span className="lg:hidden">Columns</span>
                <IconChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="relative flex flex-col gap-4 overflow-auto px-4 lg:px-6">
        <div className="overflow-hidden rounded-lg border">
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            id={sortableId}
          >
            <Table>
              <TableHeader className="bg-muted sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead 
                          key={header.id} 
                          colSpan={header.colSpan}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="**:data-[slot=table-cell]:first:w-8">
                {table.getRowModel().rows?.length ? (
                  <SortableContext
                    items={dataIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {table.getRowModel().rows.map((row) => (
                      <DraggableRow
                        key={row.id}
                        row={row}
                        onFileSelect={onFileSelect}
                        selectedFileId={selectedFileId}
                      />
                    ))}
                  </SortableContext>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

      {/* Dialog content (delete modal) is now handled by the shadcn Dialog component */}
          </DndContext>
        </div>
        <div className="flex items-center justify-between px-4">
          <div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
            {serverPagination ? (
              <>
                Showing {serverPagination.from} to {serverPagination.to} of {serverPagination.count} results
                {table.getFilteredSelectedRowModel().rows.length > 0 && (
                  <span className="ml-2">
                    ({table.getFilteredSelectedRowModel().rows.length} selected)
                  </span>
                )}
              </>
            ) : (
              <>
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </>
            )}
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit">
            <div className="hidden items-center gap-2 lg:flex">
              <Label htmlFor="rows-per-page" className="text-sm font-medium">
                Rows per page
              </Label>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value))
                }}
              >
                <SelectTrigger size="sm" className="w-20" id="rows-per-page">
                  <SelectValue
                    placeholder={table.getState().pagination.pageSize}
                  />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-fit items-center justify-center text-sm font-medium">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </div>
            <div className="ml-auto flex items-center gap-2 lg:ml-0">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <IconChevronsLeft />
              </Button>
              <Button
                variant="outline"
                className="size-8"
                size="icon"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <IconChevronLeft />
              </Button>
              <Button
                variant="outline"
                className="size-8"
                size="icon"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <IconChevronRight />
              </Button>
              <Button
                variant="outline"
                className="hidden size-8 lg:flex"
                size="icon"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <IconChevronsRight />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
